import { ActionIcon, Indicator, Modal, Text, Tooltip } from "@mantine/core";
import FilterContent from "../../../../../../../components/Filter/FilterContent";
import {
  useAccountsStore,
  useFilterStore,
} from "../../../../../../../stores/ConfigStore";
import { useFlowStore } from "../../../../../../../stores/WorkflowStore";
import { useMemo, useState } from "react";
import { Popover } from "@mui/material";
import { useLanguage } from "../../../../../../../stores/LanguageStore";
import { IconFilter } from "@tabler/icons-react";

function getRecentEmailPoolIds(nodes, startNodeId) {
  // 1. Création d'un dictionnaire pour un accès rapide
  const nodeMap = nodes.reduce((acc, node) => {
    acc[node.id] = node;
    return acc;
  }, {});

  const poolIds = new Set(); // Utilisation d'un Set pour éviter les doublons
  const visited = new Set(); // Pour éviter les boucles infinies

  const startNode = nodeMap[startNodeId];
  if (!startNode || !startNode.data || !startNode.data.source) {
    return [];
  }

  // 2. Initialisation d'une file d'attente pour gérer plusieurs chemins potentiels
  let queue = Array.isArray(startNode.data.source)
    ? [...startNode.data.source]
    : [startNode.data.source];

  // 3. Parcours de l'arbre à l'envers
  while (queue.length > 0) {
    const currentId = queue.shift();

    // Sécurité anti-boucle infinie
    if (visited.has(currentId)) continue;
    visited.add(currentId);

    const node = nodeMap[currentId];
    if (!node) continue;

    let stopBranch = false;

    // 4. Analyse du nœud s'il s'agit d'un email
    if (node.data && node.data.actionType === "email") {
      const hasPoolId =
        node.data.pool_id !== null && node.data.pool_id !== undefined;
      const hasIntegrationId =
        node.data.integration_id !== null &&
        node.data.integration_id !== undefined;

      if (hasPoolId) {
        // On a trouvé un pool_id, on l'ajoute à notre liste
        poolIds.add(node.data.pool_id);
      } else if (hasIntegrationId) {
        // Règle d'arrêt : on a un integration_id sans pool_id.
        // On arrête de chercher plus loin sur ce chemin précis.
        stopBranch = true;
      }
    }

    // 5. Continuer à remonter l'arbre si la branche n'est pas bloquée
    if (!stopBranch && node.data && node.data.source) {
      if (Array.isArray(node.data.source)) {
        queue.push(...node.data.source);
      } else {
        queue.push(node.data.source);
      }
    }
  }

  // Retourne un tableau classique à partir du Set
  return Array.from(poolIds);
}

const EventFilter = ({ id, filter }) => {
  const lang = useLanguage((s) => s.language);
  const datafieldsFilter = useFilterStore((s) => s.datafieldsFilter);
  const updateNodeData = useFlowStore((s) => s.updateNodeData);

  const handleEventFilterChange = (newFilter) => {
    updateNodeData(id, { filter: newFilter });
    handleClose();
  };

  const nodes = useFlowStore((s) => s.nodes);
  const filterFields = useMemo(() => {
    let recentEmailPoolIds = getRecentEmailPoolIds(nodes, id);

    let result = [...datafieldsFilter];

    if (recentEmailPoolIds.length > 0) {
      let emails = useAccountsStore.getState().email_accounts;
      let poolIntegrations = emails.filter((account) =>
        recentEmailPoolIds.some((poolId) =>
          account.pools?.find((pool) => pool.id === poolId),
        ),
      );

      result.unshift({
        label: "Specials",
        options: [
          {
            id: "preferred_integration_id",
            name: lang.table.toolbar.filter.filters_list_label.server
              .preferred_integration,
            option_name:
              lang.table.toolbar.filter.filters_list_label.server
                .preferred_integration,
            field: "preferred_integration_id",
            disabledCondition: true,
            type: "boolean",
            options: poolIntegrations.map((x) => ({
              label: `${x.name} (${x?.email || ""}) #${x.id}`,
              value: x.id.toString(),
            })),
          },
        ],
      });
    }
    result.unshift({
      label: "",
      options: [
        {
          id: "date_added_to_contact_list",
          name: lang.table.toolbar.filter.filters_list_label.server
            .date_added_to_contact_list,
          option_name:
            lang.table.toolbar.filter.filters_list_label.server
              .date_added_to_contact_list,
          field: "date_added_to_contact_list",
          type: "date",
        },
      ],
    });
    return result;
  }, [datafieldsFilter, id, nodes, lang]);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (e) => {
    setAnchorEl(document.getElementById("event-filter-" + id));
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip label={lang.campaign.selectors.event.use_filter}>
        <Indicator
          offset={4}
          color="red"
          withBorder
          disabled={!filter || filter?.values?.length === 0}
          styles={{
            indicator: {
              width: 12,
              height: 12,
            },
          }}
        >
          <ActionIcon
            onClick={handleOpen}
            variant={"white"}
            id={"event-filter-" + id}
            size={"lg"}
          >
            <IconFilter size={20} />
          </ActionIcon>
        </Indicator>
      </Tooltip>

      <Modal
        opened={!!anchorEl}
        onClose={handleClose}
        title={"Filtrez vos prospects"}
        size={"auto"}
      >
        <Text>Adressez cette étape à une audience cible </Text>

        <FilterContent
          mainFilter={
            filter || {
              mode: "and",
              values: [{ field_name: "1", type: "contains", value: "" }],
            }
          }
          setMainFilter={handleEventFilterChange}
          fields={filterFields}
          getFieldOptionName={(f) => f.name}
          id={id}
          pageName={"contact_lists_contacts"}
          defaultFiltersClose
          // disableEdit={disabled}
          // message={disabled ? lang.campaign.selectors.event.not_editable : ""}
        />
      </Modal>
    </>
  );
};

export default EventFilter;
